import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Capacitor } from '@capacitor/core'
import { useOrderStream } from '../hooks/useOrderStream'
import { useNotifications, useAudioAlert } from '../hooks/useNotifications'
import type {
  NotificationState,
  NotificationAction,
  NotificationSettings,
  OrderNotification,
  NewOrderEvent,
  OrderStatusEvent,
  DataChangedEvent,
  DataChangedDomain,
  ConnectionStatus,
} from '../types'
import { DEFAULT_NOTIFICATION_SETTINGS } from '../types'
import type { DeepOrder } from '@/features/orders/types'
import { isOrderAnswered } from '@/features/orders/utils'
import { setDeviceStatus, trackDeviceEvent, type DeviceEventData } from '@/lib/deviceDiagnostics'
import { setUpdateBlocker } from '@/lib/appUpdate'

type AlertStopReason = DeviceEventData['order.alert_stop']['reason']

// Storage key for settings
const SETTINGS_STORAGE_KEY = 'order-notification-settings'
const DATA_CHANGED_DEBOUNCE_MS = 250

// Load settings from localStorage
function loadSettings(): NotificationSettings {
  const browserNotificationsSupported = typeof Notification !== 'undefined' &&
    (!Capacitor.isNativePlatform() || Capacitor.getPlatform() === 'electron')
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (stored) {
      const storedSettings = JSON.parse(stored) as Partial<NotificationSettings>
      // Always force enabled=true — SSE stream must always be active
      return {
        ...DEFAULT_NOTIFICATION_SETTINGS,
        ...storedSettings,
        enabled: true,
        browserNotificationsEnabled: browserNotificationsSupported
          ? storedSettings.browserNotificationsEnabled ?? DEFAULT_NOTIFICATION_SETTINGS.browserNotificationsEnabled
          : false,
      }
    }
  } catch {
    // Ignore parse errors
  }
  return {
    ...DEFAULT_NOTIFICATION_SETTINGS,
    browserNotificationsEnabled: browserNotificationsSupported
      ? DEFAULT_NOTIFICATION_SETTINGS.browserNotificationsEnabled
      : false,
  }
}

// Save settings to localStorage
function saveSettings(settings: NotificationSettings) {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Ignore storage errors
  }
}

// Initial state
const initialState: NotificationState = {
  notifications: [],
  settings: loadSettings(),
  connectionStatus: 'disconnected',
  unreadCount: 0,
}

// Reducer
function notificationReducer(
  state: NotificationState,
  action: NotificationAction
): NotificationState {
  switch (action.type) {
    case 'ADD_NOTIFICATION': {
      const notifications = [action.payload, ...state.notifications].slice(0, 100) // Keep max 100
      return {
        ...state,
        notifications,
        unreadCount: notifications.filter((n) => !n.acknowledged).length,
      }
    }

    case 'ACKNOWLEDGE_NOTIFICATION': {
      const notifications = state.notifications.map((n) =>
        n.id === action.payload ? { ...n, acknowledged: true } : n
      )
      return {
        ...state,
        notifications,
        unreadCount: notifications.filter((n) => !n.acknowledged).length,
      }
    }

    case 'ACKNOWLEDGE_ORDERS': {
      const orderIds = new Set(action.payload)
      if (!state.notifications.some((n) => !n.acknowledged && orderIds.has(n.orderId))) {
        return state
      }
      const notifications = state.notifications.map((n) =>
        orderIds.has(n.orderId) ? { ...n, acknowledged: true } : n
      )
      return {
        ...state,
        notifications,
        unreadCount: notifications.filter((n) => !n.acknowledged).length,
      }
    }

    case 'ACKNOWLEDGE_ALL': {
      const notifications = state.notifications.map((n) => ({ ...n, acknowledged: true }))
      return {
        ...state,
        notifications,
        unreadCount: 0,
      }
    }

    case 'REMOVE_NOTIFICATION': {
      const notifications = state.notifications.filter((n) => n.id !== action.payload)
      return {
        ...state,
        notifications,
        unreadCount: notifications.filter((n) => !n.acknowledged).length,
      }
    }

    case 'CLEAR_ALL': {
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
      }
    }

    case 'SET_SETTINGS': {
      const settings = { ...state.settings, ...action.payload }
      saveSettings(settings)
      return {
        ...state,
        settings,
      }
    }

    case 'SET_CONNECTION_STATUS': {
      return {
        ...state,
        connectionStatus: action.payload,
      }
    }

    default:
      return state
  }
}

// Context value type
export interface NotificationContextValue {
  // State
  notifications: OrderNotification[]
  unreadCount: number
  settings: NotificationSettings
  connectionStatus: ConnectionStatus

  // Actions
  acknowledgeNotification: (id: string) => void
  acknowledgeAll: () => void
  removeNotification: (id: string) => void
  clearAll: () => void
  updateSettings: (settings: Partial<NotificationSettings>) => void

  // Browser notification permission
  notificationPermission: NotificationPermission
  browserNotificationsSupported: boolean
  requestNotificationPermission: () => Promise<NotificationPermission>

  // SSE connection control
  reconnect: () => void
  disconnect: () => void

  /** Called once per new order (deduped across SSE and reconciliation); returns unsubscribe. */
  subscribeNewOrders: (listener: (event: NewOrderEvent) => void) => () => void
}

// Context
const NotificationContext = createContext<NotificationContextValue | null>(null)

// Provider props
interface NotificationProviderProps {
  restaurantId: number
  children: ReactNode
}

function getDomainInvalidationKeys(
  domain: DataChangedDomain,
  restaurantId: number
): readonly unknown[][] {
  switch (domain) {
    case 'opening_times':
      return [
        ['opening-times', restaurantId],
        ['closing-times', restaurantId],
        ['holidays', restaurantId],
        ['restaurant-status', restaurantId],
      ]
    case 'menu':
      return [
        ['menu-categories', restaurantId],
        ['menu-items'],
        ['variation-categories'],
        ['variations'],
        ['shared-variation-categories', restaurantId],
        ['item-shared-variations'],
        ['allergens', restaurantId],
        ['allergens-management', restaurantId],
        ['scheduled-availability', restaurantId],
      ]
    case 'general_settings':
      return [['general-settings', restaurantId]]
    case 'delivery':
      return [['delivery-config', restaurantId]]
    case 'vouchers':
      return [
        ['voucher-groups', restaurantId],
        ['vouchers-in-group'],
        ['discount-settings', restaurantId],
      ]
    case 'holidays':
      return [['holiday-templates'], ['holiday-groups'], ['holidays', restaurantId]]
    case 'orders':
      return [['orders', restaurantId]]
    case 'reservations':
      return [
        ['reservations', restaurantId],
        ['reservation-settings', restaurantId],
        ['reservation-pauses', restaurantId],
      ]
  }
}

// Provider component
export function NotificationProvider({ restaurantId, children }: NotificationProviderProps) {
  const [state, dispatch] = useReducer(notificationReducer, initialState)
  const queryClient = useQueryClient()
  const { t } = useTranslation('generalSettings')
  const { t: tOrders } = useTranslation('orders')
  const { playAlert } = useAudioAlert()
  const {
    permission,
    requestPermission,
    showNotification,
    isSupported: browserNotificationsSupported,
  } = useNotifications()
  const navigate = useNavigate()
  const repeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const newOrderListenersRef = useRef(new Set<(event: NewOrderEvent) => void>())
  const invalidateDebounceRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  )
  // Device diagnostics: when each ringing order started ringing, why the next ones stop, and the last arrival.
  const alertStartsRef = useRef(new Map<number, number>())
  const alertStopReasonRef = useRef<AlertStopReason>('dismissed')
  const lastReceivedRef = useRef<{ id: number | null; at: number | null }>({ id: null, at: null })

  // Handle new order event
  const handleNewOrder = useCallback(
    (event: NewOrderEvent) => {
      const notification: OrderNotification = {
        id: `order-${event.orderId}-${event.timestamp}`,
        orderId: event.orderId,
        type: 'new-order',
        message: t('notifications.newOrderMessage', {
          orderId: event.orderId,
          orderType: tOrders(`orderType.${event.orderType}`),
          total: event.total.toFixed(2),
        }),
        timestamp: event.timestamp,
        acknowledged: false,
        orderType: event.orderType,
        total: event.total,
      }

      lastReceivedRef.current = { id: event.orderId, at: Date.now() }
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification })
      newOrderListenersRef.current.forEach((listener) => listener(event))

      // Build YYYY-MM-DD (local time) from the event timestamp so the orders
      // page selects the day the order was placed, not the viewer's "today".
      const orderDate = new Date(event.timestamp)
      const year = orderDate.getFullYear()
      const month = String(orderDate.getMonth() + 1).padStart(2, '0')
      const day = String(orderDate.getDate()).padStart(2, '0')
      const dateParam = `${year}-${month}-${day}`

      // Show in-app toast notification
      toast.info(notification.message, {
        duration: 10000,
        action: {
          label: t('notifications.viewAction'),
          onClick: () => {
            navigate(`/orders?orderId=${event.orderId}&date=${dateParam}`)
          },
        },
      })

      // Play sound if enabled
      if (state.settings.soundEnabled) {
        playAlert()
      }

      // Show browser notification if enabled and permitted
      if (state.settings.browserNotificationsEnabled && permission === 'granted') {
        showNotification(t('notifications.newOrderTitle'), {
          body: notification.message,
          tag: `order-${event.orderId}`,
          requireInteraction: true,
        })
      }
    },
    [
      state.settings.soundEnabled,
      state.settings.browserNotificationsEnabled,
      permission,
      playAlert,
      showNotification,
      t,
      tOrders,
      navigate,
    ]
  )

  // Handle order status event
  const handleOrderStatus = useCallback((event: OrderStatusEvent) => {
    const notification: OrderNotification = {
      id: `status-${event.orderId}-${event.timestamp}`,
      orderId: event.orderId,
      type: 'status-change',
      message: t('notifications.orderStatusMessage', { orderId: event.orderId, status: event.status }),
      timestamp: event.timestamp,
      acknowledged: false,
    }

    dispatch({ type: 'ADD_NOTIFICATION', payload: notification })
    // Any status update answers the order, so it stops ringing on every device.
    alertStopReasonRef.current = 'changed_elsewhere'
    dispatch({ type: 'ACKNOWLEDGE_ORDERS', payload: [event.orderId] })
  }, [t])

  // Cancel, confirm, decline and the old RestaurantApp publish no status event; the
  // refreshed orders list tells us once anyone answered the order (same rule as the old app).
  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success') return
      const [scope, queryRestaurantId] = event.query.queryKey as unknown[]
      const orders: unknown = event.query.state.data
      if (scope !== 'orders' || queryRestaurantId !== restaurantId || !Array.isArray(orders)) return
      const answered = (orders as DeepOrder[]).filter(isOrderAnswered).map((order) => order.id)
      if (answered.length > 0) {
        alertStopReasonRef.current = 'accepted'
        dispatch({ type: 'ACKNOWLEDGE_ORDERS', payload: answered })
      }
    })
  }, [queryClient, restaurantId])

  // Handle connection status change
  const handleConnectionChange = useCallback((status: ConnectionStatus) => {
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: status })
  }, [])

  const handleDataChanged = useCallback((event: DataChangedEvent) => {
    if (event.restaurantId > 0 && event.restaurantId !== restaurantId) {
      return
    }

    const targetRestaurantId = event.restaurantId > 0 ? event.restaurantId : restaurantId
    const debounceKey = `${targetRestaurantId}:${event.domain}`
    const existingTimer = invalidateDebounceRef.current.get(debounceKey)

    if (existingTimer) {
      clearTimeout(existingTimer)
    }

    const timeoutId = setTimeout(() => {
      const queryKeys = getDomainInvalidationKeys(event.domain, targetRestaurantId)
      queryKeys.forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey })
      })
      invalidateDebounceRef.current.delete(debounceKey)
    }, DATA_CHANGED_DEBOUNCE_MS)

    invalidateDebounceRef.current.set(debounceKey, timeoutId)
  }, [queryClient, restaurantId])

  // Connect to SSE stream
  const { connectionStatus, reconnect, disconnect } = useOrderStream({
    restaurantId,
    enabled: state.settings.enabled,
    onNewOrder: handleNewOrder,
    onOrderStatus: handleOrderStatus,
    onDataChanged: handleDataChanged,
    onConnectionChange: handleConnectionChange,
  })

  // Update connection status from hook
  useEffect(() => {
    dispatch({ type: 'SET_CONNECTION_STATUS', payload: connectionStatus })
  }, [connectionStatus])

  useEffect(() => {
    const invalidateDebounce = invalidateDebounceRef.current

    return () => {
      invalidateDebounce.forEach((timeoutId) => clearTimeout(timeoutId))
      invalidateDebounce.clear()
    }
  }, [])

  // Device diagnostics: an unacknowledged new-order notification is a ringing order.
  useEffect(() => {
    const now = Date.now()
    const ringing = new Set(
      state.notifications.filter((n) => !n.acknowledged && n.type === 'new-order').map((n) => n.orderId)
    )
    const starts = alertStartsRef.current
    for (const orderId of ringing) {
      if (!starts.has(orderId)) {
        starts.set(orderId, now)
        trackDeviceEvent('order.alert_start', { orderId })
      }
    }
    for (const [orderId, startedAt] of starts) {
      if (!ringing.has(orderId)) {
        starts.delete(orderId)
        trackDeviceEvent('order.alert_stop', {
          orderId,
          reason: alertStopReasonRef.current,
          durationMs: now - startedAt,
        })
      }
    }
    alertStopReasonRef.current = 'dismissed'
    setUpdateBlocker('ringing-orders', ringing.size > 0)
    setDeviceStatus('orders', {
      lastReceivedId: lastReceivedRef.current.id,
      lastReceivedAt: lastReceivedRef.current.at,
      alerting: ringing.size,
    })
  }, [state.notifications])

  // Repeat notifications for unacknowledged orders
  useEffect(() => {
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current)
      repeatIntervalRef.current = null
    }

    if (state.settings.repeatInterval > 0 && state.settings.soundEnabled) {
      repeatIntervalRef.current = setInterval(() => {
        const unacknowledgedNewOrders = state.notifications.filter(
          (n) => !n.acknowledged && n.type === 'new-order'
        )

        if (unacknowledgedNewOrders.length > 0) {
          playAlert()
        }
      }, state.settings.repeatInterval * 1000)
    }

    return () => {
      if (repeatIntervalRef.current) {
        clearInterval(repeatIntervalRef.current)
      }
    }
  }, [state.settings.repeatInterval, state.settings.soundEnabled, state.notifications, playAlert])

  // Actions
  const acknowledgeNotification = useCallback((id: string) => {
    dispatch({ type: 'ACKNOWLEDGE_NOTIFICATION', payload: id })
  }, [])

  const acknowledgeAll = useCallback(() => {
    dispatch({ type: 'ACKNOWLEDGE_ALL' })
  }, [])

  const removeNotification = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id })
  }, [])

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' })
  }, [])

  const updateSettings = useCallback((settings: Partial<NotificationSettings>) => {
    dispatch({ type: 'SET_SETTINGS', payload: settings })
  }, [])

  const subscribeNewOrders = useCallback((listener: (event: NewOrderEvent) => void) => {
    const listeners = newOrderListenersRef.current
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const value: NotificationContextValue = {
    // State
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    settings: state.settings,
    connectionStatus: state.connectionStatus,

    // Actions
    acknowledgeNotification,
    acknowledgeAll,
    removeNotification,
    clearAll,
    updateSettings,

    // Browser notifications
    notificationPermission: permission,
    browserNotificationsSupported,
    requestNotificationPermission: requestPermission,

    // SSE control
    reconnect,
    disconnect,

    subscribeNewOrders,
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

// Hook to use notification context
export function useNotificationContext(): NotificationContextValue {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider')
  }
  return context
}
